// src/components/OrderDetailModal.js
// Shared Modal component สำหรับแสดงรายละเอียดบิล
// ใช้ได้จากหน้า POS และ Dashboard

import React from "react";
import { useNavigate } from "react-router-dom";
import { getOrderStatusText } from "../utils/statusFormatter";

const OrderDetailModal = ({
  isOpen,
  onClose,
  orderDetail,
  loading,
  payingOrderId,
  onPay,
  formatMoney,
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const modalOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.4)",
    zIndex: 1050,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const modalDialogStyle = {
    backgroundColor: "#fff",
    borderRadius: "0.75rem",
    maxWidth: "800px",
    width: "95%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.15)",
  };

  // Calculate totals for display
  const totals = (() => {
    if (!orderDetail?.items) return { gross: 0, disc: 0 };
    let g = 0,
      d = 0;
    orderDetail.items.forEach((i) => {
      const a = Number(i.amount) || 0;
      const l = Number(i.line_discount) || 0;
      g += a + l;
      d += l;
    });
    return { gross: g, disc: d };
  })();

  return (
    <div
      style={modalOverlayStyle}
      className="pos-modal-overlay"
      onClick={onClose}
    >
      <div
        style={modalDialogStyle}
        className="p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">
            รายละเอียดบิล {orderDetail ? orderDetail.order_code : ""}
          </h5>
          <div className="d-flex gap-2">
            {orderDetail && (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary btn-ks-outline"
                  onClick={() =>
                    navigate(`/orders/${orderDetail.order_id}/print`)
                  }
                >
                  พิมพ์บิล
                </button>
                {orderDetail.status === "draft" && (
                  <button
                    type="button"
                    className="btn btn-sm btn-paid"
                    disabled={payingOrderId === orderDetail.order_id}
                    onClick={() => onPay && onPay(orderDetail)}
                  >
                    <i className="fas fa-money-bill-wave me-1"></i>
                    {payingOrderId === orderDetail.order_id
                      ? "กำลังบันทึก..."
                      : "รับชำระบิลนี้"}
                  </button>
                )}
              </>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary btn-ks-outline"
              onClick={onClose}
            >
              ปิด
            </button>
          </div>
        </div>

        {loading ? (
          <div>กำลังโหลดรายละเอียด...</div>
        ) : !orderDetail ? (
          <div className="text-muted">
            กำลังโหลดข้อมูลบิล กรุณารอสักครู่...
          </div>
        ) : (
          <>
            <div className="row mb-2">
              <div className="col-md-6">
                <dl className="row mb-2">
                  <dt className="col-sm-4">เลขบิล</dt>
                  <dd className="col-sm-8">{orderDetail.order_code}</dd>

                  <dt className="col-sm-4">สถานะ</dt>
                  <dd className="col-sm-8">{getOrderStatusText(orderDetail.status)}</dd>

                  <dt className="col-sm-4">ลูกค้า</dt>
                  <dd className="col-sm-8">
                    {orderDetail.customer_name || "-"}
                  </dd>

                  <dt className="col-sm-4">เบอร์โทร</dt>
                  <dd className="col-sm-8">
                    {orderDetail.customer_phone || "-"}
                  </dd>

                  <dt className="col-sm-4">เวลา</dt>
                  <dd className="col-sm-8">
                    {orderDetail.server_time
                      ? new Date(
                        orderDetail.server_time
                      ).toLocaleString("th-TH")
                      : orderDetail.created_at
                        ? new Date(
                          orderDetail.created_at
                        ).toLocaleString("th-TH")
                        : "-"}
                  </dd>

                  <dt className="col-sm-4">ยอดรวม</dt>
                  <dd className="col-sm-8">
                    {formatMoney(totals.gross)} ฿
                  </dd>

                  {/* Breakdown: Line Discount */}
                  {totals.disc > 0 && (
                    <>
                      <dt className="col-sm-4 text-muted small">ส่วนลดรายการ</dt>
                      <dd className="col-sm-8 text-muted small">
                        -{formatMoney(totals.disc)} ฿
                      </dd>
                    </>
                  )}

                  {/* Breakdown: Code Discount */}
                  {orderDetail.discount_code && (
                    <>
                      <dt className="col-sm-4 text-muted small">โค้ด {orderDetail.discount_code}</dt>
                      <dd className="col-sm-8 text-muted small">
                        -{formatMoney((orderDetail.discount_amount || 0) - totals.disc)} ฿
                      </dd>
                    </>
                  )}

                  {/* Total Discount */}
                  <dt className="col-sm-4 text-danger">ส่วนลดรวม</dt>
                  <dd className="col-sm-8 text-danger">
                    -{formatMoney(orderDetail.discount_amount || 0)} ฿
                  </dd>

                  <dt className="col-sm-4 fw-bold">ยอดสุทธิ</dt>
                  <dd className="col-sm-8 fw-bold text-primary">
                    {formatMoney(orderDetail.total_amount)} ฿
                  </dd>

                  <dt className="col-sm-4">การชำระ</dt>
                  <dd className="col-sm-8">
                    {orderDetail.payment_method
                      ? (
                        orderDetail.payment_method === "split"
                          ? `เงินสด ${formatMoney(orderDetail.cash_amount)} ฿ + โอน ${formatMoney(orderDetail.transfer_amount)} ฿`
                          : orderDetail.payment_method === "cash"
                            ? `เงินสด ${formatMoney(orderDetail.cash_amount)} ฿`
                            : `โอน ${formatMoney(orderDetail.transfer_amount)} ฿`
                      )
                      : "-"}
                    {orderDetail.change_amount > 0 && (
                      <div className="text-success small">ทอน {formatMoney(orderDetail.change_amount)} ฿</div>
                    )}
                    {orderDetail.deposit_usages && orderDetail.deposit_usages.length > 0 && (
                      <div className="text-muted small mt-1">
                        <i className="bi bi-wallet2 me-1"></i>
                        หักมัดจำ: {formatMoney(orderDetail.deposit_usages.reduce((s, u) => s + (Number(u.amount_used) || 0), 0))} ฿
                      </div>
                    )}
                  </dd>
                </dl>
              </div>
              <div className="col-md-6">
                <dl className="row mb-2">
                  <dt className="col-sm-4">โน้ตภายใน</dt>
                  <dd className="col-sm-8">
                    {orderDetail.internal_note || "-"}
                  </dd>
                  <dt className="col-sm-4">โน้ตลูกค้า</dt>
                  <dd className="col-sm-8">
                    {orderDetail.external_note || "-"}
                  </dd>
                </dl>
              </div>
            </div>
            <div>
              <h6 className="mt-2">รายการบริการ</h6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>บริการ</th>
                      <th>จำนวน</th>
                      <th>พนักงานหลัก</th>
                      <th>ผู้ช่วย</th>
                      <th className="text-end">ยอด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderDetail.items && orderDetail.items.length > 0 ? (
                      orderDetail.items.map((it) => (
                        <tr key={it.trans_id}>
                          <td>{it.service_name}</td>
                          <td>{it.qty}</td>
                          <td>{it.main_emp_name || "-"}</td>
                          <td>
                            {it.extra_employees &&
                              it.extra_employees.length > 0
                              ? it.extra_employees
                                .map(
                                  (e) =>
                                    `${e.emp_name || ""}${e.share_percent
                                      ? ` (${e.share_percent}%)`
                                      : ""
                                    }`
                                )
                                .join(", ")
                              : "-"}
                          </td>
                          <td className="text-end">
                            {formatMoney(it.amount)} ฿
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          ไม่พบรายการบริการ
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderDetailModal;
